import React, { useEffect, useState, useRef } from 'react';
import { Button, Card, CardBody, CardHeader, CardTitle, Col, Row, Container, Input, Label, FormGroup } from 'reactstrap';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { AesDecrypt, AesEncrypt, keyPromise } from '../../../Service/crypto';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { Editor } from '@ckeditor/ckeditor5-core';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs/Breadcrumbs';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { asBlob } from 'html-docx-js-typescript';
import { useDispatch } from 'react-redux';
import webservice from '../../../Service/webservice'
import websocket_webservice from '../../../Service/websocket_webservice'
import MasterFieldModal from './MasterFieldModal '
import api from '../../../api';

const EditTemplate: React.FC = () => {
    const Id: string | undefined = useParams().id;
    const [editorData, setEditorData] = useState<string>('');
    const [decryptedData, setDecryptedData] = useState<any[]>([]);
    const [fullTemplateData, setFullTemplateData] = useState<any[]>([]);
    const [expandedSection, setExpandedSection] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [fileName, setFileName] = useState<string>('');
    const [clientName, setClientName] = useState<any[]>([]);
    const [clientSelectedFields, setClientSelectedFields] = useState<string[]>([]);
    const editorRef = useRef<Editor | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const UserName = localStorage.UserName;
    const UserId = localStorage.id;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);
    const toggleModal = () => setModalOpen(prev => !prev);

    const handleTemplatePartChange = (newContent: any) => {
        setEditorData(newContent);
    };

    useEffect(() => {
        const fetchData = async () => {
            const key = await keyPromise
            const EncParams = await AesEncrypt(key, UserId);
            try {
                const response = await api.fetch("api/TemplateCreation/get_patient_details", {
                    method: 'POST',
                    body: JSON.stringify({ EncParams }),
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch data');
                }
                const result = await response.json();
                const decryptedResult = await AesDecrypt(key, result);
                if (decryptedResult.message === 'There is no Record Found') {
                    setDecryptedData([]);
                    setFullTemplateData([]);
                } else {
                    const entries = Object.entries(decryptedResult[0]);
                    setDecryptedData(entries);
                    setFullTemplateData(entries);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        // Initial fetch of patient data
        fetchData();
        // WebSocket setup
        const socket = new WebSocket(`${websocket_webservice}ws/Tempalatedata/`);
        // socket.onopen = () => console.log('WebSocket connected');
        // socket.onclose = () => console.log('WebSocket closed');
        // socket.onerror = (error) => console.error('WebSocket error:', error);
        socket.onmessage = () => {
            // const change = JSON.parse(event.data);
            // console.log('WebSocket message received:', change);
            // Re-fetch patient data when a WebSocket message is received
            fetchData();
        };
        // Clean up the WebSocket connection when the component unmounts
        return () => {
            if (socket.readyState === 1) { // <-- This is important
                socket.close();
            }
        }
    }, []);

    useEffect(() => {
        const fetchTemplateData = async () => {
            const response = await get_Template_Data(Id);
            const FileName = response[0].file_name;
            const ClientName = response[0].ClientName;
            const encodedContent = response[0].encoded_doc_content;
            const decodedText = atob(encodedContent);
            // Extract the HTML portion inside the MHT part
            const htmlMatch = decodedText.match(/Content-Location: file:\/\/\/.*?\n\n([\s\S]+?)\n------=mhtDocumentPart--/);
            const htmlContent = htmlMatch ? htmlMatch[1] : 'Unable to extract preview content';
            const templateData = htmlContent
                .replace(/<style[\s\S]*?<\/style>/gi, '') // Remove embedded CSS blocks so they don't show as text in the editor
                .replace(/<head[\s\S]*?<\/head>/gi, '')
                .replace(/(?:\.[\w-]+\s*\{[^{}]*\}\s*)+/g, '') // Remove CSS rules that were saved as plain text (e.g. ".boxed-answer { ... }")
                .replace(/<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
                .replace(/\$/g, '');
            setEditorData(templateData);
            setFileName(FileName);
            setClientName(ClientName);

            // Store the client's selected fields if available
            if (response[0].selectedFields) {
                setClientSelectedFields(response[0].selectedFields);
            }
        };
        fetchTemplateData();
    }, [dispatch]);

    // Effect to filter sidebar fields based on client's selected fields
    useEffect(() => {
        if (!Array.isArray(clientSelectedFields) || fullTemplateData.length === 0 || clientSelectedFields.length === 0) {
            // If we have full data but no selection, keep full data (or clear it? original code showed full)
            // But if we have selection, we should filter.
            // If selection is empty, maybe fallback to all? NewTemplateCreation falls back to empty.
            // Let's stick to NewTemplateCreation logic: if selection exists, filter.
            // If clientSelectedFields empty, check if we should show all or none. 
            // Original edit code showed all if decryptedResult found. 
            // Let's assume if we have selectedFields, we filter.
            return;
        }

        const filteredData = fullTemplateData.map(([sectionKey, sectionValue]) => {
            const fields = Array.isArray(sectionValue) ? sectionValue : Object.values(sectionValue);
            const filteredFields = fields.filter((field: any) => {
                const fieldName = typeof field === 'object' ? field.field_name : field;
                return clientSelectedFields.includes(fieldName);
            });
            return [sectionKey, filteredFields];
        }).filter(([_, fields]) => (fields as any[]).length > 0);

        setDecryptedData(filteredData);
    }, [fullTemplateData, clientSelectedFields]);

    const get_Template_Data = async (Id: string | undefined) => {
        const key = await keyPromise;
        const EncParams = await AesEncrypt(key, Id ?? '');
        const response = await api.fetch('api/TemplateCreation/GetPreviewData', {
            method: 'POST',
            body: JSON.stringify({ EncParams })
        }).then((response) => response.json());
        const TempData = await AesDecrypt(key, response);
        return TempData;
    };

    const handleSubmit = async () => {
        let isValid = true;
        if (!isValid) return;

        const pattern = /<p>(?!&)(.*?)<\/p>(?!;)/g;

        // Replace : with :$ and </p> with $</p>
        // const transformedData = editorData.replace(/:([^<]*)<\/p>/g, ':$$$1$</p>')

        let transformedData = editorData.replace(/:([^<]*)<\/p>/g, ':$$$1$</p>');
        // Replace all occurrences of double dollar signs ($$) with a single dollar sign ($)
        transformedData = transformedData.replace(/\$\$/g, '$');

        const editorDataCleaned = transformedData
            .replace(/<p>&nbsp;<\/p>/g, '') // Remove empty paragraph tags with non-breaking spaces
            .replace(/<p>/g, '') // Remove opening paragraph tags
            .replace(/<\/p>/g, ''); // Remove closing paragraph tags

        const splitData = editorDataCleaned
            .split('$') // Split the string by the $ symbol
            .filter(item => item.trim() !== ''); // Remove any empty strings from the result

        // Filter to get only the values (ignore labels ending with ':')
        const valuesOnly = splitData.filter(item => !item.endsWith(':'));

        // Extract all codes like D1234
        const historyUsageMatches = transformedData.match(/\$D\d{4}_History_usage\$/g) || [];
        const historyUsageCodes = historyUsageMatches
            .map(code => {
                const match = code.match(/D\d{4}/g);
                return match ? match[0] : undefined;
            })
            .filter((code): code is string => typeof code === 'string');
        // Filter out codes from uniqueMatches if they exist in historyUsageCodes
        const historyBaseCodes = historyUsageCodes.map(code => code.split('_')[0]);
        const matchesCodes = transformedData.match(/D\d{4}/g);
        // Use Array.from to convert Set to an array and remove duplicates
        const uniqueMatches = Array.from(new Set(matchesCodes));

        const replacements = [
            { from: "$D0220/D0230$", to: "$D0220/D0230$" },
            { from: "$D9110 Palliative$", to: "$D9110 Palliative$" },
            { from: "$D2391 to D2394$", to: "$D2391 to D2394$" },
            { from: "$D8080 Ortho percentage Coverage$", to: "$D8080 Ortho percentage Coverage$" },
            { from: "$D9944 Night Guard$", to: "$D9944 Night Guard$" },
            { from: "$D9211/ D9212 Anesthesia$", to: "$D9211/ D9212 Anesthesia$" },
            { from: "$D9922/D9923$", to: "$D9922/D9923$" },
            { from: "$D8080 ortho_Age limit$", to: "$D8080 ortho_Age limit$" },
            { from: "$D1110/D1120 MAX AGE ALLOWED?$", to: "$D1110/D1120 MAX AGE ALLOWED?$" },
        ];

        let replacedData = transformedData; // initialize with original data

        replacements.forEach(({ from, to }) => {
            replacedData = replacedData.replace(from, to);
        });

        asBlob(replacedData, {}).then((data: any) => {
            const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
            const fieldWithInstruction: any = {};
            fullTemplateData.forEach(([sectionKey, sectionValue]) => {
                const fields = Array.isArray(sectionValue) ? sectionValue : Object.values(sectionValue);
                const matchedFields = fields.filter((field: any) => {
                    const fieldName = typeof field === 'object' ? field.field_name : field;
                    return valuesOnly.includes(fieldName);
                }).map((field: any) => {
                    if (typeof field === 'object') {
                        return { field_name: field.field_name, instruction: field.instruction || "" };
                    } else {
                        return { field_name: field, instruction: "" };
                    }
                });
                if (matchedFields.length > 0) {
                    fieldWithInstruction[sectionKey] = matchedFields;
                }
            });

            const formData = new FormData();
            formData.append("username", UserName);
            formData.append("userId", UserId);
            formData.append("id", Id ?? "");
            formData.append("codes", JSON.stringify(uniqueMatches));
            formData.append("historyusages", JSON.stringify(historyBaseCodes));
            formData.append("file", blob, "template.docx");
            formData.append("Field_with_instruction", JSON.stringify(fieldWithInstruction));

            setIsLoading(true); // Set loading state to true
            axios.post(webservice + "api/TemplateCreation/update_save_word", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
                .then((response) => {
                    const auditLogData = [
                        {
                            field: "User Name",
                            value: localStorage.UserName,
                        },
                        {
                            field: "KEYS",
                            value: valuesOnly,
                        },
                    ];
                    const Username = localStorage.UserName;
                    const Refid = response.data
                    const Data = { auditLogData, Username, Refid };

                    api.fetch('api/TemplateCreation/updateauditlog', {
                        method: 'POST',
                        body: JSON.stringify({ Data }),
                    });

                    setIsLoading(false); // Set loading state to false
                    Swal.fire({
                        title: "Insurance Verification Platform",
                        html: '<span style="color: green;">Template Updated Successfully!</span>',
                        icon: "success",
                        customClass: {},
                        showClass: {
                            popup: "animate__animated animate__flipInX",
                        },
                        buttonsStyling: true,
                        allowOutsideClick: false,
                    }).then((result) => {
                        if (result.value) {
                            navigate("/templateCreation");
                        }
                    });
                })
                .catch((error) => {
                    setIsLoading(false); // Ensure loading state is turned off in case of an error
                    console.error('Error uploading file:', error);
                });
        });
    }

    const toggleSection = (sectionKey: string) => {
        if (expandedSection === sectionKey) {
            setExpandedSection(null);
        } else {
            setExpandedSection(sectionKey);
        }
    };

    const renderFieldNames = (sectionValue: any, sectionKey: string) => {
        const uniqueKeys = new Set<string>();
        const renderKeys = (keys: string[]) => {
            return keys.map((key) => {
                if (!uniqueKeys.has(key)) {
                    uniqueKeys.add(key);
                    return (
                        <li key={key}
                            draggable
                            className="section-title"
                            onDragStart={(e) => handleDragStart(e, key, sectionKey)}
                        >
                            <div className="field-name">
                                &#8226; {key}
                            </div>
                        </li>
                    );
                }
                return null;
            });
        };

        return (
            <ul>
                {sectionValue.map((item: any, index: number) => {
                    const fieldName = typeof item === 'object' ? item.field_name : item;
                    return (
                        <li key={index}>
                            <strong>{renderKeys([fieldName])}</strong>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const handleDragStart = (event: React.DragEvent, data: string, sectionKey: string) => {
        const currentContent = editorRef.current ? editorRef.current.getData() : '';
        const sectionKeyEncoded = sectionKey
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        const sectionAlreadyInEditor = currentContent.includes(sectionKeyEncoded) || currentContent.includes(sectionKey);
        if (sectionAlreadyInEditor) {
            event.dataTransfer.setData('text/html', `<p>${data}:${data}</p>`);
            event.dataTransfer.setData('text/plain', `${data}:${data}`);
        } else {
            event.dataTransfer.setData('text/html', `<p><strong>${sectionKey}</strong></p><p>${data}:${data}</p>`);
            event.dataTransfer.setData('text/plain', `${sectionKey}\n${data}:${data}`);
        }
    };

    const handleDrop = (event: DragEvent, editor: Editor) => {
        event.preventDefault();
        const data = event.dataTransfer?.getData('text/plain');
        if (data) {
            const viewFragment = editor.data.processor.toView(data);
            const modelFragment = editor.data.toModel(viewFragment);
            editor.model.insertContent(modelFragment, editor.model.document.selection);
        }
    };

    const handleDragOver = (event: DragEvent) => {
        event.preventDefault();
    };

    const filterData = (data: any[], query: string) => {
        if (!query) return data;
        return data.filter(([key, value]) =>
            key.toLowerCase().includes(query.toLowerCase()) ||
            (typeof value === 'object' &&
                Object.keys(value).some((field) => field.toLowerCase().includes(query.toLowerCase())))
        );
    };

    const renderDataCard = () => {
        const filteredData = filterData(decryptedData, searchQuery).filter(([key]) => key !== '_id');
        return (
            <>
                <Breadcrumbs mainTitle='Edit Template' parent='Template Creation' title='Edit Template' to={`/templateCreation`} />
                <Container fluid>
                    <style>
                        {`
                        .ck-editor__editable {
                            min-height: 640px !important;
                            padding: 10px;
                        }
                        .ck-editor-card {
                            margin: 20px;
                            padding: 20px;
                            width: 100%;
                        }
                        .section-title {
                            cursor: pointer;
                            font-weight: bold;
                        }
                        .field-name {
                            margin-left: 20px;
                        }
                        .half-width {
                            width: 60%;
                        }
                        .flex-container {
                            display: flex;
                            width: 100%;
                        }
                        .scrollable-editor {
                            height: 730px; /* Adjust the height as needed */
                            overflow-y: auto;
                        }
                        .ck-editor__editable_inline {
                            height: 100%; /* Ensure the editor content takes the full height of its container */
                        }
                        .card-body ul {
                            padding-left: 20px; /* Moves array data slightly to the right */
                        }
                        .card-body li {
                            margin-bottom: 10px; /* Adds space between each value */
                        }
                        .loading-overlay {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            position: fixed;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            background-color: rgba(0, 0, 0, 0.5);
                            z-index: 10000;
                        }
                        .circle {
                            width: 20px;
                            height: 20px;
                            margin: 5px;
                            border-radius: 50%;
                            background-color: #000;
                            animation: bounce 0.6s infinite alternate;
                        }
                        @keyframes bounce {
                            to {
                                transform: translateY(-50%);
                            }
                        }
                    `}
                    </style>
                    <Row>
                        <Col sm={5}>
                            {isLoading && (
                                <div >
                                    <div className="loading-overlay">
                                        <div id="page">
                                            <div id="container">
                                                <div id="ring"></div>
                                                <div id="ring"></div>
                                                <div id="ring"></div>
                                                <div id="ring"></div>
                                                <div id="h3" style={{ color: "white" }}>Loading...</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <Card style={{ minHeight: '726px', boxShadow: '0 4px 8px rgba(0,0,0,0.3)', borderRadius: '8px', marginTop: '20px' }}>
                                <br />
                                <Row className="px-4 pt-3">
                                     <div>
                                        <Button color="primary" onClick={() => window.history.back()}>
                                            Back
                                        </Button>
                                    </div>
                                    <br />
                                    <Col xs="12">
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            {/* { (localStorage.Role === 'Dev Admin' || localStorage.Role === 'Super Admin') && (
                                                <div>
                                                    <Button onClick={toggleModal} color="primary">
                                                        Add Master Field
                                                    </Button>
                                                    <MasterFieldModal isOpen={modalOpen} toggle={toggleModal} />
                                                </div>
                                            )} */}
                                            <div style={{ minWidth: 180, marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                <Label for="fileName" style={{ marginBottom: 0, fontWeight: 'bold' }}>File Name :</Label>
                                                <Input
                                                    type="text"
                                                    value={fileName}
                                                    readOnly
                                                    placeholder="File Name"
                                                    style={{ width: 200 }}
                                                    disabled
                                                />
                                                <Label for="Client Name" style={{ marginBottom: 0, fontWeight: 'bold' }}>Client Name :</Label>
                                                <Input
                                                    type="text"
                                                    value={clientName}
                                                    readOnly
                                                    placeholder="Client Name"
                                                    style={{ width: 200 }}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row style={{ marginTop: '25px' }}>
                                    <Col md={7}>
                                        <Input
                                            type="text"
                                            placeholder="Search"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            style={{ maxWidth: '66%', marginLeft: "6%" }}  // Ensure it takes full width of the column
                                        />
                                    </Col>
                                    {/* <Col md={4}>
                                        <Link to='/managelabel'>
                                            <Button color="primary" style={{ width: '100%' }}>
                                                Manage label
                                            </Button>
                                        </Link>
                                    </Col> */}
                                </Row>
                                <CardHeader>
                                    <h1><center>Drag & Drop the below fields</center></h1>
                                </CardHeader>
                                <CardBody className="card-body" style={{ marginLeft: "18%", height: "400px", overflowY: "auto" }}>
                                    {filteredData.map(([sectionKey, sectionValue]: [string, any], index: number) => (
                                        <React.Fragment key={index}>
                                            <CardTitle style={{ fontSize: '20px' }} tag="h4" className="section-title" onClick={() => toggleSection(sectionKey)}>
                                                <FontAwesomeIcon icon={expandedSection === sectionKey ? faCaretDown : faCaretRight} />
                                                &nbsp;
                                                {sectionKey}
                                            </CardTitle>
                                            &nbsp;
                                            {expandedSection === sectionKey && renderFieldNames(sectionValue, sectionKey)}
                                        </React.Fragment>
                                    ))}
                                </CardBody>
                                <Button
                                    onClick={handleSubmit}
                                    style={{ maxWidth: '25%', minHeight: '35px', position: 'relative', left: '72%', bottom: '32px' }}>
                                    Submit</Button>
                            </Card>
                        </Col>
                        <Col sm={7}>
                            <Card className="ck-editor-card scrollable-editor">
                                <CKEditor
                                    editor={ClassicEditor as any}
                                    data={editorData}
                                    onChange={(event: any, editor: any) => {
                                        const newContent = editor.getData();
                                        handleTemplatePartChange(newContent);
                                    }}
                                    onReady={(editor: Editor) => {
                                        editorRef.current = editor;
                                        const viewDocument = editor.editing.view.document;
                                        viewDocument.on('dragover', (event: any) => {
                                            if (event.domEvent instanceof DragEvent) {
                                                handleDragOver(event.domEvent);
                                            }
                                        });
                                        viewDocument.on('drop', (event: any) => {
                                            if (event.domEvent instanceof DragEvent) {
                                                handleDrop(event.domEvent, editor);
                                            }
                                        });
                                    }}
                                />
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    };
    return renderDataCard();
};

export default EditTemplate;